/* eslint-disable @typescript-eslint/no-explicit-any */

// DEPENDENCIES
import * as Sentry from "@sentry/react";
import { FormikProps, FormikValues } from "formik";

// TYPES
import {
  ComponentStep,
  DraftUUID,
  MidgardQuestion,
  StepOnePF,
  StepThreePF,
  StepTwoPF,
} from "../../types";
import { AppDispatch } from "../../app/store";

// UTILS
import {
  getQuestions,
  goToStep,
  isDev,
  isTokenActive,
  parseBirthDate,
  parseGenderToInitial,
  parseTelephone,
} from "../../app/utils";
import { getDraft, getDrafts } from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import {
  addSendedStep,
  onUpdateFormData,
  onUpdateHasExternalInformation,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import { replaceValidations } from "../../reducers/validationsReducer";
import { getDniFromCuit, getUnificationParams } from "../../utils";

// COMPONENTS
import TokenStep from "../../components/TokenStep";

import styles from "../../components/SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer.module.scss";

const tokenStep = ({
  step,
}: {
  step: number;
}): Omit<ComponentStep, "step"> => ({
  name: "Mail",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const onAuthorize = async (draftUUID?: string) => {
      try {
        if (!draftUUID || !state || !formik)
          throw new Error("DraftUUID or state not found");

        const remoteDraft = await getRemoteOnboardingDraft(draftUUID);
        if (remoteDraft) {
          autocompleteRemoteData(dispatch, formik, remoteDraft);
        } else {
          const { cuit } = formik.values;
          const escoData = await getEscoData(cuit);
          if (escoData) {
            autocompleteEscoData(dispatch, formik, escoData);
          }
        }
      } catch (error) {
        console.log(error, "Error on authorize");
        Sentry.captureException(error);
      }
    };

    const onSuccess = async () => {
      logAnalyticsAndHotjarEvent("step_mail_completed_pf");

      const { type } = state.infoStep;
      if (type !== null) {
        dispatch(onUpdateStep("information-step"));
      } else {
        dispatch(
          onUpdateFormData({
            identityDocument: getDniFromCuit(formik.values.cuit),
          })
        );
        goToStep({ dispatch, step: step + 1 });
      }
    };

    return (
      <TokenStep
        previousStep={step - 1}
        onSuccess={onSuccess}
        onAuthorize={onAuthorize}
        formik={formik}
      />
    );
  },
  disableBackNavigation: true,
  supplementaryComponentInfo: {
    show: (state) => {
      const token = state?.authentication?.token;
      return !!(token && !isTokenActive({ token }));
    },
    position: "top",
    component: () => {
      return (
        <p className={styles.emailValidation}>
          Validá tu dirección de mail para retomar el proceso de registro
        </p>
      );
    },
  },
});

const getEscoData = async (cuit: string) => {
  let steps = null;
  try {
    const draftsResponse = await getDrafts({
      cuit,
      personType: "PHYSICAL",
    });
    const draftSteps = draftsResponse?.data?.steps;
    if (draftSteps?.length > 0) steps = draftSteps;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error, "Error on getDrafts");
  }
  return steps;
};

const getRemoteOnboardingDraft = async (draftUUID: DraftUUID) => {
  let steps = null;
  try {
    const draftResponse = await getDraft({
      draftUUID,
    });
    const draftSteps = draftResponse.data.steps;
    if (draftSteps.length > 0) steps = draftSteps;
  } catch (error) {
    console.log("Error on getDraft", error);
  }
  return steps;
};

const autocompleteEscoData = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  escoData: any
) => {
  await completeSteps({
    dispatch,
    formik,
    steps: escoData,
    isActiveAccount: true,
  });
  dispatch(onUpdateHasExternalInformation(true));
  dispatch(onUpdateInfoStep("ACTIVE_ACCOUNT"));
};

const autocompleteRemoteData = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  remoteDraft: any
) => {
  await completeSteps({ dispatch, formik, steps: remoteDraft });
  dispatch(onUpdateInfoStep("PENDING_REGISTRY_REMOTE"));
};

const completeSteps = async ({
  dispatch,
  formik,
  steps,
  isActiveAccount,
}: {
  dispatch: AppDispatch;
  formik: FormikProps<FormikValues>;
  steps: any;
  //Has information in ESCO
  isActiveAccount?: boolean;
}) => {
  const { stepOne, stepOneParsed } = getStepOne({ steps, isActiveAccount });
  const { stepTwo, stepTwoParsed } = getStepTwo({ steps, isActiveAccount });
  const { stepThree, stepThreeParsed } = await getStepThree({ steps });

  const newValues = {
    ...stepOneParsed,
    ...stepTwoParsed,
    ...stepThreeParsed,
  };

  formik?.setValues(
    (prevValues) => ({
      ...prevValues,
      ...newValues,
    }),
    false
  );

  const stepOneToStore = {
    ...stepOne,
    personalInformation: {
      ...stepOne.personalInformation,
      birthDate: stepOne.personalInformation.birthDate,
    },
    referrer: formik.values.referrer,
    accountIdToLink: isDev
      ? stepOne.accountIdToLink || getUnificationParams().accountId || null
      : null,
    "@type": "1_PHYSICAL",
  };

  dispatch(addSendedStep(stepOneToStore));
  dispatch(onUpdateFormData(newValues));

  if (stepTwo) {
    const stepTwoToStore = { ...stepTwo, "@type": "2_PHYSICAL" };
    dispatch(addSendedStep(stepTwoToStore));
  }
  if (stepThree) {
    const stepThreeToStore = { ...stepThree, "@type": "3_PHYSICAL" };
    dispatch(addSendedStep(stepThreeToStore));
  }
  if (stepOne.validations) dispatch(replaceValidations(stepOne.validations));

  return { stepOneParsed, stepOne, stepTwoParsed, stepTwo };
};

const getStepOne = ({
  steps,
  isActiveAccount,
}: {
  steps: any;
  isActiveAccount?: boolean;
}) => {
  const stepOne = steps.find(
    (step: StepOnePF) => step["@type"] === "1_PHYSICAL"
  );

  const {
    accountIdToLink,
    personalInformation: {
      birthDate,
      birthCountry,
      citizenship,
      civilStatus,
      cuit,
      gender,
      name,
      pep,
      pepDetails,
      telephone,
      lastName,
      identityDocument,
      referrer,
      addresses,
      spouse,
    },
  } = stepOne;

  const parsePep = (pep: string) => {
    // Esco doesn't give us specific information about pep, so return empty string
    return pep ?? (isActiveAccount ? "" : "NONE");
  };

  const stepOneParsed = {
    birthDate: parseBirthDate(birthDate),
    birthCountry: birthCountry,
    citizenship: citizenship,
    civilStatus: civilStatus,
    cuit: cuit,
    gender: parseGenderToInitial(gender),
    lastName: lastName,
    name: name,
    pep: parsePep(pep),
    pepMotive: pepDetails,
    telephone: parseTelephone(telephone),
    identityDocument: identityDocument?.idNumber || getDniFromCuit(cuit),
    tramitNumber: identityDocument?.procedureId,
    referredBool: referrer !== null ? true : false,
    addresses: {},
    spouseName: undefined,
    spouseLastName: undefined,
    spouseCuil: undefined,
    accountIdToLink: isDev
      ? accountIdToLink || getUnificationParams().accountId || null
      : null,
  };

  if (addresses && addresses.length > 0) {
    stepOneParsed.addresses = {
      zipCode: addresses[0].zipCode,
      state: addresses[0].state,
      municipality: addresses[0].city,
      fullAddress: `${addresses[0].street} ${addresses[0].number} ${addresses[0].city}`,
      floor: addresses[0].floor,
      apartment: addresses[0].apartment,
      country: addresses[0].country,
    };
  }

  if (spouse) {
    stepOneParsed.spouseName = spouse.name;
    stepOneParsed.spouseLastName = spouse.lastName;
    stepOneParsed.spouseCuil = spouse.cuit;
  }

  return { stepOne, stepOneParsed };
};

const getStepTwo = ({
  steps,
  isActiveAccount,
}: {
  steps: any;
  isActiveAccount?: boolean;
}) => {
  const stepTwo = steps.find(
    (step: StepTwoPF) => step["@type"] === "2_PHYSICAL"
  );
  let stepTwoParsed;

  if (stepTwo) {
    const {
      employmentProfile: {
        activities,
        bankAccounts,
        employmentStatus,
        foreignTaxDetail,
        years,
      },
    } = stepTwo;

    //For the moment, the 'activities' coming from ESCO (endpoint getDrafts) are emptied because they are not parsed from the backend
    const parsedActivities =
      isActiveAccount || activities.length === 0 ? "" : activities;

    const foreignTaxDetailParsed = {
      idNumber: foreignTaxDetail?.idNumber,
      country: foreignTaxDetail?.address?.country,
      state: foreignTaxDetail?.address?.state,
      fullAddress: `${foreignTaxDetail?.address?.street} ${foreignTaxDetail?.address?.number} ${foreignTaxDetail?.address?.city}`,
      municipality: foreignTaxDetail?.address?.city,
      zipCode: foreignTaxDetail?.address?.zipCode,
      floor: foreignTaxDetail?.address?.floor,
      apartment: foreignTaxDetail?.address?.apartment,
    };

    stepTwoParsed = {
      foreignTaxDetailsBool: foreignTaxDetail ? "SI" : "NO",
      bankAccountsBool: bankAccounts && bankAccounts.length > 0 ? "SI" : "NO",
      employmentProfile: {
        activities: parsedActivities,
        bankAccounts: bankAccounts ? bankAccounts : "",
        employmentStatus,
        ...(foreignTaxDetailParsed
          ? { foreignTaxDetails: foreignTaxDetailParsed }
          : {}),
        years,
      },
    };
  }

  return { stepTwo, stepTwoParsed };
};

const getStepThree = async ({ steps }: { steps: any }) => {
  const stepThree = steps.find(
    (step: StepThreePF) => step["@type"] === "3_PHYSICAL"
  );
  let stepThreeParsed;

  const questions = await getQuestions();

  const answers: string[] = stepThree?.investmentProfile?.choices || [];

  const investmentProfileParsed = () => {
    const result: Record<string, string> = {};
    questions.forEach((question) => {
      const res = findAnswerAboutQuestion(question);
      if (res) {
        result[question.externalCode] = res.option;
      }
    });
    return result;
  };

  const findAnswerAboutQuestion = (question: MidgardQuestion) =>
    question.choices.find((choice) =>
      answers.find((answer) => answer === choice.externalCode)
    );

  if (stepThree) {
    stepThreeParsed = {
      investmentProfile: investmentProfileParsed(),
    };
  }

  return { stepThree, stepThreeParsed };
};

export default tokenStep;
